{
  "id": "rocket",
  "name": "Rocket Chip",
  "kind": "generator",
  "spotlight": true,
  "url": "https://github.com/chipsalliance/rocket-chip",
  "origin": "UC Berkeley, CHIPS Alliance",
  "authors": [
    "UC Berkeley",
    "SiFive",
    "CHIPS Alliance"
  ],
  "started": 2014,
  "language": "Chisel, Scala",
  "license": "Apache-2.0, BSD-3-Clause",
  "isa": "RV64GC",
  "archived": false,
  "sources": [
    {
      "label": "rocket-chip README",
      "url": "https://github.com/chipsalliance/rocket-chip"
    },
    {
      "label": "The Rocket Chip Generator, UCB/EECS-2016-17",
      "url": "https://www2.eecs.berkeley.edu/Pubs/TechRpts/2016/EECS-2016-17.html"
    },
    {
      "label": "Chipyard documentation",
      "url": "https://chipyard.readthedocs.io"
    },
    {
      "label": "RISC-V Debug Specification",
      "url": "https://riscv.org/technical/specifications/"
    }
  ],
  "blocks": [
    {
      "k": "h",
      "text": "Overview"
    },
    {
      "k": "table",
      "headers": [
        "Specification",
        "Value"
      ],
      "rows": [
        [
          "ISA support",
          "RV64GC, with RV32 configurations available from the same generator"
        ],
        [
          "Microarchitecture",
          "Five-stage in-order, single-issue pipeline with virtual memory and coherent caches"
        ],
        [
          "Performance density",
          "2.32 CoreMark/MHz"
        ],
        [
          "Resource footprint",
          "No fixed LUT count; area depends on the generated configuration"
        ],
        [
          "Clock domain",
          "25\u2013100 MHz on Xilinx Zynq-7000 FPGAs, depending on configuration (2016 report)"
        ]
      ]
    },
    {
      "k": "p",
      "text": "Rocket Chip is a configurable generator for RISC-V systems on chip (SoCs), integrating processor cores with caches, memory controllers and interconnects within a single hardware design. With it, developers can generate systems with different architectures and resource requirements."
    },
    {
      "k": "p",
      "text": "Its main core, Rocket, is in-order, which means it executes instructions in the order the program lists them. Rocket has a five-stage pipeline, an arrangement where each instruction passes through five steps and several instructions are in progress at the same time. It implements the 64-bit RISC-V instruction set with a memory management unit, being able to boot operating systems. The design is written in Chisel, a hardware construction language built on the Scala programming language."
    },
    {
      "k": "h",
      "text": "Microarchitecture"
    },
    {
      "k": "photo",
      "src": "/images/processors/rocket/rocketchip-diagram.png",
      "text": "A Rocket Chip system on chip containing the core, its caches and the TileLink interconnect.",
      "credit": "Chipyard documentation (BSD-3-Clause)",
      "href": "https://github.com/ucb-bar/chipyard/blob/main/docs/_static/images/rocketchip-diagram.png"
    },
    {
      "k": "p",
      "text": "Rocket uses a five-stage pipeline capable of dispatching one instruction per cycle. The generator exposes configurable parameters for cache sizes, hart count, floating-point support and memory management. The specifications below correspond to a specific Rocket configuration."
    },
    {
      "k": "table",
      "headers": [
        "Part",
        "Rocket"
      ],
      "rows": [
        [
          "Pipeline",
          "Five stages, in program order"
        ],
        [
          "Issue",
          "One instruction per cycle"
        ],
        [
          "L1 caches",
          "Separate instruction and data caches, sized by configuration"
        ],
        [
          "Virtual memory",
          "MMU with page-table walks when enabled"
        ],
        [
          "Interconnect",
          "TileLink"
        ],
        [
          "Floating point",
          "Optional FPU, part of the G extension"
        ]
      ]
    },
    {
      "k": "h",
      "text": "Memory and register map"
    },
    {
      "k": "p",
      "text": "The SoC generator assigns physical address ranges to main memory and memory-mapped peripherals during system integration. The generated device tree records these ranges, along with peripheral addresses and interrupt connections. Firmware and operating systems use this information to identify and initialize the available hardware."
    },
    {
      "k": "code",
      "title": "Reading the map",
      "code": "# on the running system\ndtc -I fs -O dts /proc/device-tree | grep -n -E \"memory@|serial@|clint@|plic@\"\n\n# on a firmware image that carries its device tree\ndtc -I dtb -O dts -o soc.dts soc.dtb\ngrep -n \"reg = <\" soc.dts",
      "comments": [
        null,
        null,
        null,
        null,
        null,
        null
      ]
    },
    {
      "k": "callout",
      "variant": "note",
      "text": "Chipyard's default configuration defines a reference memory map. Generated systems may use different addresses, which are recorded in the device tree."
    },
    {
      "k": "table",
      "headers": [
        "Region",
        "Owner"
      ],
      "rows": [
        [
          "Boot ROM",
          "System integration, first instructions after reset"
        ],
        [
          "CLINT and PLIC",
          "Interrupt controllers of the system"
        ],
        [
          "UART and other peripherals",
          "System peripherals"
        ],
        [
          "DRAM",
          "Board memory"
        ]
      ]
    },
    {
      "k": "h",
      "text": "Physical implementation"
    },
    {
      "k": "p",
      "text": "Rocket is implemented in hardware through FPGA mapping or ASIC synthesis. Its RTL can be configured and synthesized for different target platforms. A 2016 technical report demonstrated FPGA implementations on Xilinx Zynq-7000 boards, operating at 25-100 MHz depending on the configuration."
    },
    {
      "k": "table",
      "headers": [
        "Target",
        "Reported figure"
      ],
      "rows": [
        [
          "FPGA",
          "25 to 100 MHz on Zynq-7000, depending on configuration (2016 report)"
        ],
        [
          "ASIC",
          "No fixed area or frequency, the flow and node decide"
        ],
        [
          "Area on FPGA",
          "No published LUT count for the core alone"
        ],
        [
          "Process node",
          "Chosen by whoever builds the chip"
        ]
      ]
    },
    {
      "k": "h",
      "text": "Identifying the part"
    },
    {
      "k": "p",
      "text": "Rocket cores are not necessarily identified by the Rocket name in the system. Check the SoC documentation for references to Rocket Chip or Rocket Core, and inspect the hardware configuration and source files for Rocket-specific identifiers. The device tree and hardware identification registers can provide additional information about the processor's implementation."
    },
    {
      "k": "table",
      "headers": [
        "Where it appears",
        "How to confirm"
      ],
      "rows": [
        [
          "Chipyard-built systems",
          "Device tree compatible strings and the memory map"
        ],
        [
          "Early SiFive cores",
          "SiFive documentation for the specific part"
        ],
        [
          "Research chips",
          "Papers and the project's release notes"
        ]
      ]
    },
    {
      "k": "h",
      "text": "Disassembly and debugging"
    },
    {
      "k": "p",
      "text": "Use readelf -A to inspect the firmware's .riscv.attributes section and identify its target instruction set. Use objdump -d to disassemble the binary and inspect the instructions being executed. Compare these instructions against the extensions supported by the processor, available through its identification registers or SoC documentation. "
    },
    {
      "k": "code",
      "title": "Tools",
      "code": "riscv64-unknown-elf-readelf -A firmware.elf\nriscv64-unknown-elf-objdump -d -M no-aliases firmware.elf\n\n# LLVM toolchains\nllvm-readelf --arch-specific firmware.elf\nllvm-objdump -d --mattr=+m,+a,+f,+d,+c firmware.elf",
      "comments": [
        null,
        null,
        null,
        null,
        null,
        null
      ]
    },
    {
      "k": "p",
      "text": "The identification registers report the same values from the running system. A binary can target a larger instruction set than the core implements."
    },
    {
      "k": "table",
      "headers": [
        "Item",
        "Value"
      ],
      "rows": [
        [
          "ISA",
          "RV64GC in the default configuration, RV32 is also generated"
        ],
        [
          "Compiler flags",
          "-march=rv64gc -mabi=lp64d"
        ],
        [
          "Debug transport",
          "RISC-V Debug 0.13 over JTAG, when the system includes a debug module"
        ],
        [
          "Reset address",
          "Set by the system integration, not by the core"
        ],
        [
          "misa (0x301)",
          "ISA and enabled extensions"
        ],
        [
          "mvendorid (0xF11)",
          "JEDEC vendor ID, read-only"
        ],
        [
          "marchid (0xF12)",
          "microarchitecture ID, read-only"
        ],
        [
          "mimpid (0xF13)",
          "implementation version, read-only"
        ],
        [
          "mhartid (0xF14)",
          "hart that runs the code, read-only"
        ]
      ]
    },
    {
      "k": "h",
      "text": "History"
    },
    {
      "k": "p",
      "text": "Rocket Chip comes from UC Berkeley, from the group that designed the RISC-V instruction set. Krste Asanovic and colleagues described it in the 2016 technical report \"The Rocket Chip Generator\", and Berkeley built many research chips with it."
    },
    {
      "k": "p",
      "text": "SiFive, a company founded by creators of RISC-V, based its first commercial cores on Rocket. CHIPS Alliance, an open hardware group of the Linux Foundation, now hosts the project."
    },
    {
      "k": "h",
      "text": "Where it is used"
    },
    {
      "k": "p",
      "text": "Most users reach Rocket through Chipyard, a framework that combines Rocket, BOOM and other blocks into complete chips. Chipyard runs those chips in software simulation, on FPGAs or through manufacturing tools. An FPGA is a chip that can be reconfigured to behave like a different circuit, and researchers use it to test a design at real speed before manufacturing."
    }
  ],
  "seoTitle": "Rocket Chip, RISC-V core generator, pipeline and memory map",
  "seoDescription": "Rocket Chip is a five-stage in-order core generator with configurable caches, MMU and TileLink interconnect, used on FPGAs and in ASIC flows, with device tree maps and firmware identification."
}
